import { Link } from 'react-router-dom';
import { useFeaturedRaffles } from '../hooks/useRaffles';
import { Button } from '../components/ui';
import RaffleCard, { RaffleCardSkeleton, RaffleCardGrid } from '../components/raffle/RaffleCard';
import EmptyState from '../components/shared/EmptyState';
import SEO from '../components/shared/SEO';

/**
 * Home Page - Glassmorphism Design
 *
 * Clean hero, "Why Raflr" value proposition, How It Works,
 * and featured items grid.
 */
export default function Home() {
  const { raffles, loading, error } = useFeaturedRaffles(8);

  return (
    <div>
      <SEO
        title="Skill-Based Competitions for Local Items in Ireland"
        description="Win amazing deals on electronics, gaming, fashion & more. Enter skill-based competitions, collect locally in Ireland. Secure payments, protected by escrow."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-midnight min-h-[85vh] lg:min-h-[90vh] flex items-center">
        {/* Background effects */}
        <div className="absolute inset-0 bg-mesh-dark" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-petrol/5 rounded-full blur-[120px]" />

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />

        <div className="page-container relative z-10 py-12 lg:py-0">
          <div className="grid lg:grid-cols-[1.2fr,1fr] gap-8 lg:gap-12 items-center">
            {/* Left: Text Content */}
            <div className="text-center lg:text-left">
              <h1 className="font-display font-bold text-3xl sm:text-4xl md:text-6xl lg:text-7xl text-cream mb-6 lg:mb-8 leading-[1.1] tracking-tight whitespace-nowrap">
                A new way to <span className="text-primary">buy & sell</span>
              </h1>

              <p className="text-lg md:text-xl text-cream/60 mb-8 lg:mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                The Irish-born marketplace where great items find great buyers.
                <span className="text-cream/80"> Secure payments, local pickup, fair prices.</span>
              </p>

              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Link to="/browse">
                  <Button size="lg" className="shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow">
                    Start Buying
                  </Button>
                </Link>
                <Link to="/create">
                  <Button variant="outline" size="lg">
                    Start Selling
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Card Preview - Mobile */}
            <div className="lg:hidden relative h-[280px] mb-4 flex justify-center">
              <div className="absolute top-0 right-0 w-32 h-32 bg-petrol/30 rounded-full blur-3xl" />
              <div className="absolute bottom-4 left-4 w-24 h-24 bg-primary/20 rounded-full blur-3xl" />

              <div className="relative w-[220px] h-full">
                {/* Back card */}
                <div className="absolute top-0 left-1/2 w-[160px] bg-white/5 backdrop-blur-md border border-white/10 rounded-lg overflow-hidden shadow-lg rotate-6 opacity-50" style={{ transform: 'translateX(-50%) translateX(16px) rotate(6deg)' }}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400&h=300&fit=crop" alt="Car" className="w-full h-full object-cover" />
                  </div>
                </div>

                {/* Middle card */}
                <div className="absolute top-6 left-1/2 w-[170px] bg-white/5 backdrop-blur-md border border-white/10 rounded-lg overflow-hidden shadow-lg opacity-70" style={{ transform: 'translateX(-50%) rotate(-3deg)' }}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&h=300&fit=crop" alt="Sofa" className="w-full h-full object-cover" />
                  </div>
                </div>

                {/* Front card */}
                <div className="absolute top-12 left-1/2 w-[180px] bg-white/5 backdrop-blur-md border border-primary/30 rounded-lg overflow-hidden shadow-2xl" style={{ transform: 'translateX(-50%) translateX(-8px) rotate(2deg)' }}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=400&h=300&fit=crop" alt="Gaming Console" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-2.5">
                    <span className="text-2xs font-bold uppercase tracking-wider text-primary-light">Electronics</span>
                    <h3 className="font-semibold text-cream text-xs mt-0.5">PS5 + 2 Controllers</h3>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-base font-bold text-primary">€5</span>
                      <span className="text-2xs text-cream/50 font-mono">42/100</span>
                    </div>
                    <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-1.5">
                      <div className="h-full bg-primary rounded-full" style={{ width: '42%' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Card Preview - Desktop */}
            <div className="relative hidden lg:block h-[450px]">
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-petrol/30 rounded-full blur-[80px]" />
              <div className="absolute -bottom-10 -left-10 w-56 h-56 bg-primary/20 rounded-full blur-[60px]" />
              <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-accent/20 rounded-full blur-[40px]" />

              {/* Back card */}
              <div className="absolute top-0 right-0 w-72 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden shadow-lg transform rotate-6 translate-x-4 -translate-y-2 hover:rotate-3 transition-transform duration-500">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400&h=300&fit=crop" alt="Car" className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <span className="text-2xs font-bold uppercase tracking-wider text-primary-light">Vehicles</span>
                  <h3 className="font-semibold text-cream mt-1">2019 Ford Focus</h3>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-lg font-bold text-primary">€10</span>
                    <span className="text-xs text-cream/50">156/500</span>
                  </div>
                </div>
              </div>

              {/* Middle card */}
              <div className="absolute top-8 right-8 w-72 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden shadow-lg transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&h=300&fit=crop" alt="Sofa" className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <span className="text-2xs font-bold uppercase tracking-wider text-primary-light">Furniture</span>
                  <h3 className="font-semibold text-cream mt-1">Green Velvet Sofa</h3>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-lg font-bold text-primary">€3</span>
                    <span className="text-xs text-cream/50">89/150</span>
                  </div>
                </div>
              </div>

              {/* Front card */}
              <div className="absolute top-16 right-16 w-72 bg-white/5 backdrop-blur-md border border-primary/30 rounded-xl overflow-hidden shadow-xl shadow-primary/10 transform rotate-2 hover:-rotate-1 hover:shadow-primary/20 transition-all duration-500">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=400&h=300&fit=crop" alt="Gaming Console" className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <span className="text-2xs font-bold uppercase tracking-wider text-primary-light">Electronics</span>
                  <h3 className="font-semibold text-cream mt-1">PS5 + 2 Controllers</h3>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-lg font-bold text-primary">€5</span>
                    <span className="text-xs text-cream/50">42/100</span>
                  </div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-primary rounded-full" style={{ width: '42%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Raflr */}
      <section className="relative py-20 md:py-28 bg-midnight-light overflow-hidden">
        <div className="absolute inset-0 bg-mesh-dark opacity-30" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-[120px]" />

        <div className="page-container relative z-10">
          {/* Pain point intro */}
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-cream/40 text-lg mb-4">
              <span>Tired of lowballers?</span>
              <span className="text-cream/20">•</span>
              <span>Endless messages?</span>
              <span className="text-cream/20">•</span>
              <span>Competitions with 50,000 entries?</span>
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-cream">
              There's a better way.
            </h2>
          </div>

          {/* Value Props - Two columns */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* For Sellers */}
            <div className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-br from-accent/20 to-transparent rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 h-full">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h3 className="font-display font-bold text-xl text-cream">For Sellers</h3>
                </div>
                <p className="text-accent text-sm mb-6">Get what your item is actually worth</p>
                <ul className="space-y-3">
                  {[
                    'Set your price — no negotiations',
                    'Multiple buyers enter, you keep the total',
                    'Often earn more than marketplace value',
                    'We handle payments securely',
                  ].map((text, i) => (
                    <li key={text} className="flex items-start gap-3">
                      <svg className="w-5 h-5 flex-shrink-0 text-accent/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={i < 2 ? 'text-cream text-sm' : 'text-cream/60 text-sm'}>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* For Buyers */}
            <div className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 h-full">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                  </div>
                  <h3 className="font-display font-bold text-xl text-cream">For Buyers</h3>
                </div>
                <p className="text-primary text-sm mb-6">Get items at a fraction of retail</p>
                <ul className="space-y-3">
                  {[
                    'Enter competitions for just a few euros',
                    'Get items worth much more than you paid',
                    'Local pickup — see it before you take it',
                    'Full refund if competition is cancelled',
                  ].map((text, i) => (
                    <li key={text} className="flex items-start gap-3">
                      <svg className="w-5 h-5 flex-shrink-0 text-primary/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={i < 2 ? 'text-cream text-sm' : 'text-cream/60 text-sm'}>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative py-20 md:py-28 bg-midnight overflow-hidden">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent/5 rounded-full blur-[100px]" />

        <div className="page-container relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-cream mb-4">
              How It Works
            </h2>
            <p className="text-cream/60 max-w-lg mx-auto">
              Whether buying or selling, get started in minutes
            </p>
          </div>

          {/* Two-column layout */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {/* For Buyers */}
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/20 to-primary/5 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-primary/10 backdrop-blur rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl md:text-2xl text-cream">For Buyers</h3>
                    <p className="text-primary text-sm">Find great deals locally</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {[
                    { step: 1, title: 'Browse Items', description: 'Electronics, furniture, fashion & more' },
                    { step: 2, title: 'Answer & Enter', description: 'Answer a skill-based question, pay securely' },
                    { step: 3, title: 'Collect', description: 'Arrange pickup with the seller' },
                  ].map(({ step, title, description, note }, index) => (
                    <div key={step} className="flex items-start gap-4">
                      <div className="relative">
                        <div className="w-10 h-10 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-center">
                          <span className="text-primary font-bold font-mono">{step}</span>
                        </div>
                        {index < 2 && (
                          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-primary/30 to-transparent" />
                        )}
                      </div>
                      <div className="pt-1">
                        <h4 className="font-semibold text-cream mb-1">{title}</h4>
                        <p className="text-cream/50 text-sm">{description}</p>
                        {note && (
                          <p className="text-primary/60 text-xs mt-1.5">{note}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* For Sellers */}
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-accent/20 to-accent/5 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-accent/10 backdrop-blur rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl md:text-2xl text-cream">For Sellers</h3>
                    <p className="text-accent text-sm">Turn items into cash</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {[
                    { step: 1, title: 'List Your Item', description: 'Photos, price & entry quantity' },
                    { step: 2, title: 'Collect Entries', description: 'Payments held securely until handover' },
                    { step: 3, title: 'Get Paid', description: 'Funds released to your bank' },
                  ].map(({ step, title, description }, index) => (
                    <div key={step} className="flex items-start gap-4">
                      <div className="relative">
                        <div className="w-10 h-10 bg-accent/10 border border-accent/20 rounded-xl flex items-center justify-center">
                          <span className="text-accent font-bold font-mono">{step}</span>
                        </div>
                        {index < 2 && (
                          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-accent/30 to-transparent" />
                        )}
                      </div>
                      <div className="pt-1">
                        <h4 className="font-semibold text-cream mb-1">{title}</h4>
                        <p className="text-cream/50 text-sm">{description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Trust indicators - subtle inline */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 mt-12 pt-8 border-t border-white/5">
            {[
              { icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z', text: 'Secure payments' },
              { icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', text: 'Buyer protection' },
              { icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z', text: 'Local pickup' },
              { icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', text: 'Verified sellers' },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-cream/40">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
                </svg>
                <span className="text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Items */}
      <section className="py-20 bg-midnight-light">
        <div className="page-container">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
            <div>
              <h2 className="font-bold text-2xl md:text-3xl text-cream">
                Live Now
              </h2>
              <p className="text-cream/60 mt-2">
                Items available to enter right now
              </p>
            </div>
            <Link to="/browse" className="text-primary font-semibold text-sm hover:text-primary-light transition-colors">
              Browse All →
            </Link>
          </div>

          {loading && (
            <RaffleCardGrid>
              {[...Array(4)].map((_, i) => (
                <RaffleCardSkeleton key={i} />
              ))}
            </RaffleCardGrid>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-cream/50">Unable to load competitions</p>
            </div>
          )}

          {!loading && !error && raffles.length === 0 && (
            <EmptyState
              title="No items listed yet"
              description="Be the first to list something for sale!"
              actionLabel="List an Item"
              onAction={() => window.location.href = '/create'}
            />
          )}

          {!loading && raffles.length > 0 && (
            <RaffleCardGrid>
              {raffles.map((raffle, i) => (
                <RaffleCard key={raffle.id} raffle={raffle} featured={i === 0} />
              ))}
            </RaffleCardGrid>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-28 bg-midnight overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-accent/10 rounded-full blur-[120px]" />

        <div className="page-container relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-accent/10 backdrop-blur rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h2 className="font-display font-bold text-3xl md:text-4xl text-cream mb-4">
              Got something to sell?
            </h2>
            <p className="text-cream/60 mb-8 max-w-lg mx-auto leading-relaxed">
              List in minutes. No haggling, no hassle. Just set your price and let buyers come to you.
            </p>

            <Link to="/create">
              <Button variant="accent" size="lg" className="shadow-lg shadow-accent/25">
                List an Item
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
